<template>
  <div class="policy-wrapper">
    <div class="policy" :class="[
      `policy--${policyStatusColor}`,
      { 'policy--expanded': expanded },
    ]">
      <div class="row row--policy box box--default" :class="{ 'row--active': expanded }">
        <div class="row__data row__data--medium row__data--fixed"
          :class="{ 'row__data--underline': $checkRolePermission('policy_invoice_edit') }">
          <span v-if="$checkRolePermission('policy_invoice_edit')" @click="showStatusPopup = true"
            style="cursor: pointer">
            <transition name="fade" mode="in-out">
              <PopupEditer v-if="showStatusPopup" @togglePopup="showStatusPopup = $event" :items="policyStatuses"
                :selected="policyStatus" :label="'name'" @submit="updatePolicyStatus($event)" />
            </transition>
            {{ policyStatus }}
          </span>

          <span v-else>
            {{ policyStatus }}
          </span>
        </div>

        <div class="row__data row__data--medium">{{ policyNumber }}</div>
        <div class="row__data row__data--medium">{{ createdAt }}</div>
        <div class="row__data row__data--medium policy__company">
          <span>{{ company }}</span>
        </div>
        <div class="row__data row__data--large">
          {{ branch }}
        </div>
        <div class="row__data row__data--medium">
          {{ premiumInterval }}
        </div>
        <div v-if="$checkRolePermission('policy_invoice_create')"
          class="row__data row__data--medium row__data--add-accident icon__add--active" @click="showInvoiceModal()">
        </div>
        <div v-else style="cursor:no-drop;"
          class="row__data row__data--medium row__data--add-accident icon__add--active"></div>
        <div class="row__data row__data--medium row__data--half row__data--last">
          <div class="row__data row__data--small" :class="[
            { 'row__data--rotate': expanded },
            { 'row__data--more': policy.invoices.length },
          ]" @click="policy.invoices.length ? expandInvoice(policy.id) : null"></div>
        </div>
      </div>
      <div class="policy-wrapper__payments" ref="accidents" :style="{
        maxHeight: expanded ? `${containerMaxHeight}px` : '0px',
      }">
        <InvoicesTable :invoices="policy.invoices" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import statics from "@/statics";
import PopupEditer from "@/partials/PopupEditer";
import InvoicesTable from "@/components/AccountDetails/InvoicesTable";
import { formatDisplayDate } from "@/helpers/dateFormatters";
import { fetchGlobalCustomer } from "@/helpers/accountDetailHelpers";

defineOptions({
  name: "PolicyInvoice"
});

const props = defineProps({
  policy: {
    required: true,
    type: Object,
  },
  expandInvoice: {
    required: true,
    type: Function,
  },
  expandedInvoices: {
    required: true,
    type: Array,
  },
});

const store = useStore();
const route = useRoute();

// State
const showStatusPopup = ref(false);
const headerOffset = ref(36);

// Computed
const language = computed(() => store.getters.language);
const policyForms = computed(() => store.getters.policyForms);

const expanded = computed(() => props.expandedInvoices.includes(props.policy.id));

const policyStatus = computed(() =>
  props.policy.policy_status ? props.policy.policy_status.name : statics.empty
);

const policyNumber = computed(() => props.policy.policy_number || statics.empty);

const branch = computed(() => {
  if (props.policy.policy_services.length) {
    const branches = props.policy.policy_services
      .map((service) => service.service?.branch_category?.long_de || "")
      .filter(Boolean);
    return [...new Set(branches)].join(" / ") || statics.empty;
  }
  return statics.empty;
});

const company = computed(() =>
  props.policy.company ? (props.policy.company.full_name || statics.empty) : statics.empty
);

const createdAt = computed(() =>
  props.policy.created_at ? formatDisplayDate(props.policy.created_at) : statics.empty
);

const policyStatusColor = computed(() => {
  if (props.policy.policy_status) {
    const status = statics.policyStatuses.find((s) => props.policy.policy_status.id === s.id);
    return status ? status.name : null;
  }
  return null;
});

const policyStatuses = computed(() => policyForms.value["policy-statuses"]);

const premiumInterval = computed(() => {
  if (props.policy.policy_services.length) {
    const lastService = props.policy.policy_services[props.policy.policy_services.length - 1];
    if (lastService.premium_interval) {
      return lastService.premium_interval[`long_${language.value}`] || statics.empty;
    }
  }
  return statics.empty;
});

const containerMaxHeight = computed(() =>
  props.policy.invoices.length * statics.paymentHeight + headerOffset.value
);

// Methods
const updatePolicyStatus = async (policyStatusId) => {
  await store.dispatch("updatePolicyStatus", {
    policyId: props.policy.id,
    policyStatusId,
  });
  await fetchGlobalCustomer(route.params.id);
  showStatusPopup.value = false;
};

const showInvoiceModal = () => {
  store.commit("setInvoicePolicyId", props.policy.id);
  store.commit("setShowAddInvoiceModal", true);
};
</script>